import {
  DEFAULT_HTTP_TIMEOUT,
  FULFILL_HTTP_TIMEOUT,
  VITE_FF_SERVICE_URL,
  VITE_HUB_SERVICE_URL,
  VITE_PLUGIN_SERVICE_URL,
  VITE_UPS_SERVICE,
} from '@/constants/env';

import type { IReqCatalogs } from './catalogs/reqCatalogs';
import type { IReqCreateOrder } from './createOrder/reqCreateOrder';
import type { IReqDashboard } from './dashboard/reqDashboard';
import type { IReqInventory } from './Fulfill/Inventory/reqInventory';
import type { IReqHistoryOrders } from './Fulfill/inventoryHistory/reqHistory';
import type { IReqLotTracking } from './Fulfill/LotTracking/reqLotTracking';
import type { IReqStatusOrders } from './Fulfill/StatusOrders/reqStatusOrders';
import type { IReqFulfillUsers } from './Fulfill/Users/reqUsers';
import type { IReqWarehouses } from './Fulfill/Warehouses/reqWarehouses';
import type { IReqNotifications } from './notifications/reqNotifications';
import type { IRequestOrders } from './orders/reqOrders';
import type { IReqEnvioFlex } from './plugins/EnvioFlex/reqEnvioFlex';
import type { IReqFalabella } from './plugins/Falabella/reqFalabella';
import type { IReqMLFulfill } from './plugins/MLFulfill/reqMLFulfill';
import type { IReqMLParcel } from './plugins/MLParcel/reqMLParcel';
import type { HttpProviderContext } from './typesHttp';
import type { IReqUPS } from './ups/reqUPS';
import type { IReqUserData } from './userData/reqUserData';
import type { IReqUserSession } from './userSession/reqUserSession';
import type { IReqWebhooks } from './webhooks/reqWebhooks';
import type { KyInstance } from 'ky';

import {
  createHttpInstance,
  initializeFFServices,
  initializeHubServices,
  initializePluginServices,
  initializeUpsServices,
} from './utilsHttp';

let httpFF: KyInstance | null = null;
let httpHUB: KyInstance | null = null;
let httpUPS: KyInstance | null = null;
let httpPlugins: KyInstance | null = null;

let userSession: IReqUserSession | null = null;
let userData: IReqUserData | null = null;
let catalogs: IReqCatalogs | null = null;
let createOrder: IReqCreateOrder | null = null;
let notifications: IReqNotifications | null = null;
let orders: IRequestOrders | null = null;
let webhooks: IReqWebhooks | null = null;
let dashboard: IReqDashboard | null = null;

let inventory: IReqInventory | null = null;
let statusOrder: IReqStatusOrders | null = null;
let historyOrder: IReqHistoryOrders | null = null;
let warehouses: IReqWarehouses | null = null;
let fulfillUsers: IReqFulfillUsers | null = null;
let lotTracking: IReqLotTracking | null = null;

let ups: IReqUPS | null = null;

let envioFlex: IReqEnvioFlex | null = null;
let falabella: IReqFalabella | null = null;
let mlFulfill: IReqMLFulfill | null = null;
let mlParcel: IReqMLParcel | null = null;

export function initializeHttpProvider(): HttpProviderContext {
  if (!httpFF || !httpHUB || !httpUPS) {
    const upsToken = localStorage.getItem('upsToken');

    httpFF = createHttpInstance(VITE_FF_SERVICE_URL, null, FULFILL_HTTP_TIMEOUT, true, {
      'x-api-version': 'auth0',
    });
    httpHUB = createHttpInstance(VITE_HUB_SERVICE_URL, null, DEFAULT_HTTP_TIMEOUT, true);
    httpUPS = createHttpInstance(VITE_UPS_SERVICE, upsToken);
    httpPlugins = createHttpInstance(VITE_PLUGIN_SERVICE_URL, null, undefined, true);

    // Initialize services
    const hubServices = initializeHubServices(httpHUB);
    const ffServices = initializeFFServices(httpFF);
    const upsServices = initializeUpsServices(httpUPS);
    const pluginServices = initializePluginServices(httpPlugins);

    // Assign services to variables
    ({ userSession, userData, catalogs, createOrder, notifications, orders, webhooks, dashboard } =
      hubServices);

    ({ inventory, statusOrder, historyOrder, warehouses, fulfillUsers, lotTracking } = ffServices);

    ({ ups } = upsServices);

    ({ envioFlex, falabella, mlFulfill, mlParcel } = pluginServices);
  }

  if (
    !userSession ||
    !userData ||
    !catalogs ||
    !createOrder ||
    !notifications ||
    !orders ||
    !webhooks ||
    !dashboard
  ) {
    throw new Error('Failed to initialize HUB services');
  }

  if (!inventory || !statusOrder || !historyOrder || !warehouses || !fulfillUsers || !lotTracking) {
    throw new Error('Failed to initialize FF services');
  }

  if (!ups) {
    throw new Error('Failed to initialize UPS services');
  }

  if (!envioFlex || !falabella || !mlFulfill || !mlParcel) {
    throw new Error('Failed to initialize Plugin services');
  }

  return {
    userSession,
    userData,
    catalogs,
    createOrder,
    notifications,
    orders,
    webhooks,
    dashboard,
    inventory,
    statusOrder,
    historyOrder,
    warehouses,
    fulfillUsers,
    lotTracking,
    ups,
    envioFlex,
    falabella,
    mlFulfill,
    mlParcel,
  };
}

/**
 * Reset Fulfill HTTP instances and services, used when tokenV3 needs to be refreshed
 * HUB99-1344
 */
export function resetHttpFFInstance(): void {
  httpFF = null;
  inventory = null;
  statusOrder = null;
  historyOrder = null;
  warehouses = null;
  fulfillUsers = null;
  lotTracking = null;
}
